import { useState, useMemo } from "react";
import { toast } from "sonner";
import {
  PlusCircle,
  Building,
  MapPin,
  Calendar,
  FileText,
  CheckCircle,
  Search,
} from "lucide-react";
import { useStore } from "../../../store/useStore";

const AddJobTab = () => {
  const [jobData, setJobData] = useState({
    title: "",
    description: "",
    company: "",
    location: "",
    requirements: "",
    startDate: "",
    endDate: "",
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [companySearch, setCompanySearch] = useState("");
  const [locationSearch, setLocationSearch] = useState("");
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);

  const seedData = useStore((s) => s.seedData);

  // استخراج قائمة الشركات من seedData
  const { companiesList } = useMemo(() => {
    const names = [];
    if (seedData && Array.isArray(seedData.companies)) {
      seedData.companies.forEach((sector) => {
        if (Array.isArray(sector.companies)) {
          sector.companies.forEach((c) => {
            const name = (c.ar || c.name || c.title || "").toString();
            if (name && !names.includes(name)) names.push(name);
          });
        }
      });
    }
    return { companiesList: names.sort() };
  }, [seedData]);

  // استخراج المحافظات والمراكز من seedData
  const allLocations = useMemo(() => {
    const locations = [];
    if (seedData && seedData.governorates && Array.isArray(seedData.governorates)) {
      seedData.governorates.forEach(gov => {
        // إضافة المحافظة
        locations.push(gov.name);
        // إضافة المراكز
        if (gov.centers && Array.isArray(gov.centers)) {
          gov.centers.forEach(center => {
            locations.push(`${center} - ${gov.name}`);
          });
        }
      });
    }
    return locations.sort();
  }, [seedData]);

  // تصفية الشركات بناءً على البحث
  const filteredCompanies = useMemo(() => {
    if (!companySearch) return companiesList;
    return companiesList.filter(company =>
      company.toLowerCase().includes(companySearch.toLowerCase())
    );
  }, [companiesList, companySearch]);

  // تصفية المواقع بناءً على البحث
  const filteredLocations = useMemo(() => {
    if (!locationSearch) return allLocations;
    return allLocations.filter(location =>
      location.toLowerCase().includes(locationSearch.toLowerCase())
    );
  }, [allLocations, locationSearch]);

  // الحصول على تاريخ اليوم بصيغة YYYY-MM-DD
  const getTodayDate = () => {
    return new Date().toISOString().split("T")[0];
  };

  const handleCompanySelect = (company) => {
    setJobData(prev => ({ ...prev, company }));
    setCompanySearch("");
    setShowCompanyDropdown(false);
  };

  const handleLocationSelect = (location) => {
    setJobData(prev => ({ ...prev, location }));
    setLocationSearch("");
    setShowLocationDropdown(false);
  };

  const handleCompanyInputChange = (e) => {
    const value = e.target.value;
    setCompanySearch(value);
    setJobData(prev => ({ ...prev, company: value }));
    setShowCompanyDropdown(true);
    
    if (errors.company) {
      setErrors(prev => ({ ...prev, company: "" }));
    }
  };

  const handleLocationInputChange = (e) => {
    const value = e.target.value;
    setLocationSearch(value);
    setJobData(prev => ({ ...prev, location: value }));
    setShowLocationDropdown(true);
    
    if (errors.location) {
      setErrors(prev => ({ ...prev, location: "" }));
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setJobData((prev) => ({ ...prev, [name]: value }));

    // مسح الخطأ عند التعديل
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // التحقق من الحقول المطلوبة
    if (!jobData.title.trim()) newErrors.title = "المسمى الوظيفي مطلوب";
    if (!jobData.description.trim())
      newErrors.description = "الوصف الوظيفي مطلوب";
    if (!jobData.company.trim()) newErrors.company = "اسم الشركة مطلوب";
    if (!jobData.startDate) newErrors.startDate = "تاريخ البداية مطلوب";
    if (!jobData.endDate) newErrors.endDate = "تاريخ الانتهاء مطلوب";

    // التحقق من التواريخ
    if (jobData.startDate) {
      const startDate = new Date(jobData.startDate);
      const today = new Date(getTodayDate());

      if (startDate < today) {
        newErrors.startDate = "تاريخ البداية لا يمكن أن يكون قبل اليوم";
      }
    }

    if (jobData.startDate && jobData.endDate) {
      const startDate = new Date(jobData.startDate);
      const endDate = new Date(jobData.endDate);

      if (endDate <= startDate) {
        newErrors.endDate = "تاريخ الانتهاء يجب أن يكون بعد تاريخ البداية";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("❌ يوجد أخطاء في النموذج", {
        description: "يرجى تصحيح الأخطاء قبل المتابعة",
        duration: 4000,
      });
      return;
    }

    // تنظيف البيانات وإزالة المسافات الزائدة
    const payload = {
      title: jobData.title.trim(),
      description: jobData.description.trim(),
      company: jobData.company.trim(),
      location: jobData.location.trim(),
      requirements: jobData.requirements
        .split(",")
        .map((r) => r.trim())
        .filter(Boolean),
      startDate: jobData.startDate,
      endDate: jobData.endDate,
    };

    // Get token from local storage
    const token = localStorage.getItem("adminToken");

    if (!token) {
      toast.error("❌ مصادقة مطلوبة", {
        description: "يجب تسجيل الدخول أولاً",
        duration: 4000,
      });
      return;
    }

    setLoading(true);

    try {
      const res = await fetch("https://nspo-backend-gray.vercel.app/api/jobs", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || data.error || "فشل في إضافة الوظيفة");
      }

      // Success - show success toast
      toast.success("🎉 تمت الإضافة بنجاح", {
        description: `تم إضافة وظيفة "${payload.title}" في ${payload.company}`,
        duration: 5000,
        icon: <CheckCircle className="w-5 h-5 text-green-500" />,
      });

      // Reset form
      setJobData({
        title: "",
        description: "",
        company: "",
        location: "",
        requirements: "",
        startDate: "",
        endDate: "",
      });
      setCompanySearch("");
      setLocationSearch("");
      setErrors({});
    } catch (err) {
      toast.error("❌ فشل في الإضافة", {
        description: err.message || "يرجى المحاولة مرة أخرى",
        duration: 6000,
      });
      console.error("Error adding job:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 max-w-4xl mx-auto">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="flex items-center justify-center gap-3 mb-4">
          <div className="p-3 bg-blue-100 rounded-2xl">
            <PlusCircle className="w-8 h-8 text-blue-600" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900">
            إضافة وظيفة جديدة
          </h2>
        </div>
        <p className="text-gray-600 text-lg">
          املأ النموذج أدناه لإضافة وظيفة جديدة إلى النظام
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Job Title */}
        <div
          className={`rounded-2xl p-6 border transition-all duration-200 ${
            errors.title
              ? "bg-red-50 border-red-200"
              : "bg-gray-50 border-gray-200"
          }`}
        >
          <label className="block text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
            <FileText className="w-5 h-5 text-blue-600" />
            المسمى الوظيفي *
          </label>
          <input
            type="text"
            name="title"
            value={jobData.title}
            onChange={handleChange}
            required
            className={`w-full rounded-xl p-4 text-lg focus:ring-3 transition-all duration-200 ${
              errors.title
                ? "border-red-300 focus:ring-red-500 focus:border-red-500"
                : "border-gray-300 focus:ring-blue-500 focus:border-blue-500"
            }`}
            placeholder="أدخل المسمى الوظيفي..."
          />
          {errors.title && (
            <p className="text-red-600 text-sm mt-2 flex items-center gap-2">
              <span className="w-2 h-2 bg-red-600 rounded-full"></span>
              {errors.title}
            </p>
          )}
        </div>

        {/* Description */}
        <div
          className={`rounded-2xl p-6 border transition-all duration-200 ${
            errors.description
              ? "bg-red-50 border-red-200"
              : "bg-gray-50 border-gray-200"
          }`}
        >
          <label className="block text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
            <FileText className="w-5 h-5 text-blue-600" />
            الوصف الوظيفي *
          </label>
          <textarea
            name="description"
            value={jobData.description}
            onChange={handleChange}
            required
            rows="4"
            className={`w-full rounded-xl p-4 text-lg focus:ring-3 transition-all duration-200 resize-none ${
              errors.description
                ? "border-red-300 focus:ring-red-500 focus:border-red-500"
                : "border-gray-300 focus:ring-blue-500 focus:border-blue-500"
            }`}
            placeholder="أدخل وصف الوظيفة والمهام المطلوبة..."
          />
          {errors.description && (
            <p className="text-red-600 text-sm mt-2 flex items-center gap-2">
              <span className="w-2 h-2 bg-red-600 rounded-full"></span>
              {errors.description}
            </p>
          )}
        </div>

        {/* Company & Location */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Company */}
          <div
            className={`rounded-2xl p-6 border transition-all duration-200 ${
              errors.company
                ? "bg-red-50 border-red-200"
                : "bg-gray-50 border-gray-200"
            }`}
          >
            <label className="block text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <Building className="w-5 h-5 text-green-600" />
              الشركة *
            </label>
            <div className="relative">
              <div className="relative">
                <input
                  type="text"
                  value={jobData.company}
                  onChange={handleCompanyInputChange}
                  onFocus={() => setShowCompanyDropdown(true)}
                  required
                  className={`w-full rounded-xl p-4 text-lg focus:ring-3 transition-all duration-200 pr-12 ${
                    errors.company
                      ? "border-red-300 focus:ring-red-500 focus:border-red-500"
                      : "border-gray-300 focus:ring-green-500 focus:border-green-500"
                  }`}
                  placeholder="ابحث عن الشركة..."
                />
                <Search className="w-5 h-5 text-gray-400 absolute right-4 top-1/2 transform -translate-y-1/2" />
              </div>
              
              {showCompanyDropdown && filteredCompanies.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-60 overflow-auto">
                  {filteredCompanies.map((company) => (
                    <div
                      key={company}
                      className="px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors duration-200 border-b border-gray-100 last:border-b-0"
                      onClick={() => handleCompanySelect(company)}
                    >
                      <div className="flex items-center gap-2">
                        <Building className="w-4 h-4 text-green-600" />
                        <span className="text-gray-700">{company}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {errors.company && (
              <p className="text-red-600 text-sm mt-2 flex items-center gap-2">
                <span className="w-2 h-2 bg-red-600 rounded-full"></span>
                {errors.company}
              </p>
            )}
          </div>

          {/* Location */}
          <div
            className={`rounded-2xl p-6 border transition-all duration-200 ${
              errors.location
                ? "bg-red-50 border-red-200"
                : "bg-gray-50 border-gray-200"
            }`}
          >
            <label className="block text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <MapPin className="w-5 h-5 text-purple-600" />
              موقع العمل *
            </label>
            <div className="relative">
              <div className="relative">
                <input
                  type="text"
                  value={jobData.location}
                  onChange={handleLocationInputChange}
                  onFocus={() => setShowLocationDropdown(true)}
                  
                  className={`w-full rounded-xl p-4 text-lg focus:ring-3 transition-all duration-200 pr-12 ${
                    errors.location
                      ? "border-red-300 focus:ring-red-500 focus:border-red-500"
                      : "border-gray-300 focus:ring-purple-500 focus:border-purple-500"
                  }`}
                  placeholder="ابحث عن الموقع..."
                />
                <Search className="w-5 h-5 text-gray-400 absolute right-4 top-1/2 transform -translate-y-1/2" />
              </div>
              
              {showLocationDropdown && filteredLocations.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-60 overflow-auto">
                  {filteredLocations.map((location) => (
                    <div
                      key={location}
                      className="px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors duration-200 border-b border-gray-100 last:border-b-0"
                      onClick={() => handleLocationSelect(location)}
                    >
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-purple-600" />
                        <span className="text-gray-700">{location}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {errors.location && (
              <p className="text-red-600 text-sm mt-2 flex items-center gap-2">
                <span className="w-2 h-2 bg-red-600 rounded-full"></span>
                {errors.location}
              </p>
            )}
          </div>
        </div>

        {/* Requirements */}
        <div className="bg-gray-50 rounded-2xl p-6 border border-gray-200">
          <label className="block text-lg font-semibold text-gray-800 mb-3">
            المتطلبات والمهارات
          </label>
          <input
            type="text"
            name="requirements"
            value={jobData.requirements}
            onChange={handleChange}
            placeholder="مثلاً: Node.js, MongoDB, React, إدارة المشاريع..."
            className="w-full border border-gray-300 rounded-xl p-4 text-lg focus:ring-3 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200"
          />
          <p className="text-sm text-gray-500 mt-3 flex items-center gap-2">
            <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
            اكتب المتطلبات مفصولة بفاصلة (,) وسيتم تحويلها تلقائياً إلى قائمة
          </p>
        </div>

        {/* Dates */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Start Date */}
          <div
            className={`rounded-2xl p-6 border transition-all duration-200 ${
              errors.startDate
                ? "bg-red-50 border-red-200"
                : "bg-gray-50 border-gray-200"
            }`}
          >
            <label className="block text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-red-600" />
              تاريخ البداية *
            </label>
            <input
              type="date"
              name="startDate"
              value={jobData.startDate}
              onChange={handleChange}
              required
              min={getTodayDate()}
              className={`w-full rounded-xl p-4 text-lg focus:ring-3 transition-all duration-200 ${
                errors.startDate
                  ? "border-red-300 focus:ring-red-500 focus:border-red-500"
                  : "border-gray-300 focus:ring-red-500 focus:border-red-500"
              }`}
            />
            {errors.startDate && (
              <p className="text-red-600 text-sm mt-2 flex items-center gap-2">
                <span className="w-2 h-2 bg-red-600 rounded-full"></span>
                {errors.startDate}
              </p>
            )}
          </div>

          {/* End Date */}
          <div
            className={`rounded-2xl p-6 border transition-all duration-200 ${
              errors.endDate
                ? "bg-red-50 border-red-200"
                : "bg-gray-50 border-gray-200"
            }`}
          >
            <label className="block text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-red-600" />
              تاريخ الانتهاء *
            </label>
            <input
              type="date"
              name="endDate"
              value={jobData.endDate}
              onChange={handleChange}
              required
              min={jobData.startDate || getTodayDate()}
              className={`w-full rounded-xl p-4 text-lg focus:ring-3 transition-all duration-200 ${
                errors.endDate
                  ? "border-red-300 focus:ring-red-500 focus:border-red-500"
                  : "border-gray-300 focus:ring-red-500 focus:border-red-500"
              }`}
            />
            {errors.endDate && (
              <p className="text-red-600 text-sm mt-2 flex items-center gap-2">
                <span className="w-2 h-2 bg-red-600 rounded-full"></span>
                {errors.endDate}
              </p>
            )}
          </div>
        </div>

        {/* Submit Button */}
        <div className="pt-6">
          <button
            type="submit"
            disabled={loading}
            className={`w-full py-4 rounded-2xl text-white font-bold text-lg transition-all duration-300 ${
              loading
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 shadow-lg hover:shadow-xl transform hover:scale-[1.02]"
            }`}
          >
            {loading ? (
              <div className="flex items-center justify-center gap-3">
                <div className="w-6 h-6 border-3 border-white border-t-transparent rounded-full animate-spin"></div>
                جاري إضافة الوظيفة...
              </div>
            ) : (
              <div className="flex items-center justify-center gap-3">
                <PlusCircle className="w-6 h-6" />
                إضافة الوظيفة
              </div>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddJobTab;
